import { Container, Graphics } from "pixi.js";
import { applyPlacement, fitScale, resolveResponsive } from "@slot/presentation";
import type { FitLimits, PanelFadeSurface, PresentationTaskRunner, Responsive, ViewportStore } from "@slot/presentation";
import { LocalizedText } from "../../localization/LocalizedText";
import type { LocalizationService } from "../../localization/LocalizationService";

/** The `ContinueText` block every game modal shares: 1500×118, pinned to the viewport bottom; the hint sits 280.1 above its centre (66.9 in landscape). */
const CONTINUE_TEXT = {
  width: 1500,
  height: 118,
  limits: { desktop: { maxWidth: 1500 }, mobile: { maxWidth: 1500 }, mobile_landscape: { maxWidth: 3000, referenceHeight: 118 } },
  placement: { desktop: { x: 0, y: -280.1 }, mobile: { x: 0, y: -280.1 }, mobile_landscape: { x: 0, y: -66.9 } },
} as const;
const DIM_ALPHA = 196 / 255;

/**
 * The chrome the big-win, feature-trigger and feature-finished overlays share: a full-screen dim
 * that doubles as the tap surface, a content box fitted into the viewport under per-shape limits
 * (the panel art is authored inside it), and the "Tap anywhere to continue" hint. Panels compose
 * this and add their own content to `content`, and what plays behind it to `backdrop`. The shell's
 * since decision 46 (it was slot-cannon's): a title's panel passes its own box, limits and per-shape
 * position, which follow that title's composition, and keeps its art.
 */
export class ModalChrome extends Container implements PanelFadeSurface {
  /** Full-screen dim (the feature trigger and finished views fade it). */
  public readonly dim: Graphics;
  /**
   * Over the dim, behind the content: full-screen art that plays behind the panel (the big win's coin fountain).
   * Unscaled, its origin at the viewport centre like the dim's.
   */
  public readonly backdrop: Container;
  /** Full-screen input surface ABOVE the content; `visible` gates the press. */
  public readonly skipButton: Graphics;
  /** The fitted box the panel art is authored inside. */
  public readonly content: Container;
  /** The hint's root (the overlay views fade it in and out). */
  public readonly continueTextRoot: Container;
  public readonly continueText: LocalizedText;
  private readonly runner: PresentationTaskRunner;
  /** Latest fade per target — a new fade on the same node cancels the previous. */
  private readonly fadeGeneration = new WeakMap<Container, number>();
  private unsubscribeViewport: (() => void) | null = null;

  public constructor(input: {
    label: string;
    viewport: ViewportStore;
    localization: LocalizationService;
    /** UI chrome clock — `services.chromeTaskRunner` (never speed-scaled, pauses with the ticker). */
    runner: PresentationTaskRunner;
    /**
     * The content box's authored size and per-shape fit limits, and where it sits: `y` (design units, fixed) plus
     * `shiftY` (its own units, so that part scales with it), per shape. The panels set the two by hand to keep their
     * number at the grid's centre, where a symbol win's amount label shows. In portrait the game's composition sits at
     * a fixed −200 with a box that scales with the screen's width, so a panel takes the same −200 and a `shiftY` that
     * scales its number as the grid's centre scales, and the two meet on every phone. On desktop both are drawn at a
     * fixed scale, so one shift does.
     */
    content: { width: number; height: number; y?: Responsive<number>; shiftY?: Responsive<number>; limits: Responsive<FitLimits> };
    /** Tap-anywhere hint (omit for panels without one). */
    continueText?: boolean;
  }) {
    super({ label: input.label });
    this.visible = false;
    this.runner = input.runner;
    this.dim = new Graphics();
    this.dim.label = "dim";
    this.dim.eventMode = "static"; // swallows taps under the panel even while the skip surface is hidden
    this.addChild(this.dim);
    this.backdrop = new Container({ label: "Backdrop" });
    this.addChild(this.backdrop);
    this.content = new Container({ label: "Content" });
    this.addChild(this.content);

    this.continueTextRoot = new Container({ label: "ContinueText" });
    this.continueTextRoot.alpha = 0;
    this.continueText = new LocalizedText({
      localization: input.localization,
      key: "common.tap_anywhere",
      fallback: "Tap anywhere to continue",
      style: { fill: 0xffffff, fontSize: 81.1, fontFamily: "Roboto, system-ui, sans-serif", fontWeight: "500", align: "center" },
      label: "TapAnywhereText",
    });
    this.continueText.alpha = 198 / 255;
    this.continueTextRoot.addChild(this.continueText);
    if (input.continueText !== false) this.addChild(this.continueTextRoot);

    this.skipButton = new Graphics();
    this.skipButton.label = "SkipButton";
    this.skipButton.eventMode = "static";
    this.skipButton.visible = false;
    this.addChild(this.skipButton);

    this.unsubscribeViewport = input.viewport.subscribe((v) => {
      const { width, height } = v.design;
      this.dim.clear().rect(-width / 2, -height / 2, width, height).fill({ color: 0x000000, alpha: DIM_ALPHA });
      this.skipButton.clear().rect(-width / 2, -height / 2, width, height).fill({ color: 0x000000, alpha: 0 });
      const contentScale = fitScale(input.content, resolveResponsive(input.content.limits, v.shape), v.design);
      this.content.scale.set(contentScale, contentScale);
      this.content.position.set(0, resolveResponsive(input.content.y ?? 0, v.shape) + resolveResponsive(input.content.shiftY ?? 0, v.shape) * contentScale);
      // The hint block: fitted to the viewport, bottom-pinned, the text at its per-shape spot inside.
      const hintScale = fitScale(CONTINUE_TEXT, resolveResponsive<FitLimits>(CONTINUE_TEXT.limits, v.shape), v.design);
      this.continueTextRoot.scale.set(hintScale, hintScale);
      this.continueTextRoot.position.set(0, height / 2 - (CONTINUE_TEXT.height * hintScale) / 2);
      applyPlacement(this.continueText, CONTINUE_TEXT.placement, v.shape);
    });
  }

  /**
   * Fade `target.alpha` to `alpha` over `seconds` on the chrome clock, cancelling any fade already
   * running on that node. Resolves when done (or when superseded).
   */
  public fade(target: Container, alpha: number, seconds: number): Promise<void> {
    const generation = (this.fadeGeneration.get(target) ?? 0) + 1;
    this.fadeGeneration.set(target, generation);
    if (seconds <= 0) {
      target.alpha = alpha;
      return Promise.resolve();
    }
    const from = target.alpha;
    return this.runner.tweenProgress(
      seconds,
      (k) => {
        if (this.fadeGeneration.get(target) !== generation || target.destroyed) return;
        target.alpha = from + (alpha - from) * k;
      },
      undefined,
      { easing: "linear" },
    );
  }

  /** Cancel a running fade on `target` (the node keeps its current alpha). */
  public stopFade(target: Container): void {
    this.fadeGeneration.set(target, (this.fadeGeneration.get(target) ?? 0) + 1);
  }

  public dispose(): void {
    this.unsubscribeViewport?.();
    this.unsubscribeViewport = null;
    this.destroy({ children: true });
  }
}
